import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { getObjectId } from '@hero/schema';
import type {
  Currency,
  CustomerGroupTypes,
  Fee,
  ItemPacking,
  ItemType,
  OrderAllocationStrategy,
  SavedLocation,
  VehicleTypes,
} from '@hero/schema';
import type {
  CalculatePriceMultiRoutesData,
  CreateMultiOrderAPI,
} from '@hero/api';

/**
 * Single source of truth for the publish-order flow used by both
 * carrier and customer dashboards. All "new order" components read
 * from / write to this store so that the side panel, summary, and
 * publish action stay in lockstep with the form steps.
 *
 * State is intentionally flat — we keep the form state, plus a tiny
 * "session-only" extras list for newly created saved-locations until
 * the React Query cache invalidates. Persistence (customers, saved
 * locations, the order itself) goes through `@hero/api-hooks`; this
 * store only holds the in-progress draft.
 */

export interface PublishOrderLocationDraft {
  /** SavedLocation id when picked from the CRM; undefined for ad-hoc Google Places picks. */
  savedLocationId?: string;
  /** Human-readable name shown in cards and on the map. */
  name: string;
  /** Optional full address line (reverse-geocoded or saved). */
  fullAddress?: string;
  /** City label used by the side panel summary. */
  city?: string;
  /** Latitude (decimal degrees, EPSG:4326). */
  lat: number;
  /** Longitude (decimal degrees, EPSG:4326). */
  lng: number;
  /**
   * Mirrors order destination `isAccurateLocation`. Saved locations are
   * treated as accurate; ad-hoc Google Places picks carry whatever the
   * user confirms in the picker drawer. `undefined` is treated as accurate.
   */
  isAccurate?: boolean;
  /**
   * Google Maps link the pin came from — pasted by the user or carried
   * over from the saved place. Sent as `destinations[].locationUrl` so
   * the order keeps the link, not just the coordinates read out of it.
   */
  locationUrl?: string;
  /** Optional Google Places id, kept for debugging / later geocode refresh. */
  placeId?: string;
  /**
   * Optional phone number lifted from the saved-location's **primary**
   * contact at pick time. Used by `buildCreateMultiOrderPayload` as a
   * fallback when no client-level phone is available (the receiver
   * phone input was removed from the publish UI in favor of deriving
   * it from the CRM client or the destination contact).
   */
  contactPhone?: string;
  /**
   * Display name for the location's primary contact (lifted from the
   * saved location's primary contact at pick time). Surfaced on the
   * destination/origin row so the carrier can see at a glance who
   * the driver will be calling on arrival, without opening the
   * picker again. Optional — Google picks without a saved record
   * carry no contact until the user attaches one.
   */
  contactName?: string;
}

export interface PublishOrderLoadDraft {
  /**
   * Single requested vehicle type — kept for back-compat (pricing
   * cards, save-as-default banner, side-panel chip readers). Stays
   * in sync with {@link vehicleTypes}: the setters below always
   * update both, so writers can pick either field without falling
   * out of step. Equal to `vehicleTypes[0]` in steady state.
   */
  vehicleType?: VehicleTypes;
  /**
   * Multi-type variant — every entry is an "accepted vehicle type"
   * for this load (e.g. flatbed OR curtain side). Empty/undefined
   * is treated the same as not picking any vehicle yet; the publish
   * payload falls back to org/client defaults in that case. Always
   * has length 1 when only the single field was set.
   */
  vehicleTypes?: VehicleTypes[];
  /** From `temperatureList` in `@hero/core` (e.g. `dry`, `chilled`, `frozen`). */
  temperatureType?: string;
  /** Free-form min/max in °C when temperatureType requires it. */
  temperatureDegree?: { min: number; max: number };
  itemType?: ItemType;
  packing?: ItemPacking;
  quantity?: number;
  itemDescription?: string;
  /**
   * Cargo weight in **tons** — display unit matches both the order
   * drawer's edit-load surface (`OrderLoadEditDrawer`) and the load
   * card's `convertWeightTo="ton"`. Wire format on the
   * `CreateMultiOrderAPI` payload is `{ value: weightTon, type: 'ton' }`.
   */
  weightTon?: number;
  /** Declared cargo value in the order currency. */
  itemCost?: number;
  /**
   * Proof-of-delivery copy preference for this order. Seeded from
   * `{ ...orgSettings, ...clientSettings }.pod_settings` and forwarded
   * on create as `Order.settings.podCopy`.
   */
  podSettings?: 'pod_image' | 'return_original_pod';
}

export interface PublishOrderCustomerDraft {
  /** Customer id when picked from CRM. Walk-in customers leave this undefined. */
  customerId?: string;
  name: string;
  phone?: string;
  email?: string;
  type?: CustomerGroupTypes;
}

export interface PublishOrderPickupRange {
  /** ISO date string (timezone-aware) for the start of the pickup window. */
  from: string;
  /** ISO date string for the end of the pickup window. */
  to: string;
}

/**
 * Snapshot of a publish that succeeded in the current browser session.
 * Powers the side-panel "This session" list so the carrier sees their
 * own progress without leaving the page.
 *
 * Built at success time from the store snapshot + the server response;
 * intentionally denormalised (no live joins) so list rendering stays
 * cheap and survives subsequent edits to the form.
 */
export interface PublishSessionEntry {
  /** Local id for React keys; not persisted server-side. */
  id: string;
  /** Server-issued order ids — one per route (destination). */
  orderIds: string[];
  /** ms epoch when the publish landed. */
  createdAt: number;
  /** Receiver/customer label, or undefined for internal shipments. */
  receiverName?: string;
  /**
   * `true` when this order was published as an internal shipment
   * (`Order.shipper` ships their own goods — no external client). Replaces
   * the older "walk-in" semantics: a publish must always be linked to
   * either an explicit CRM Client or to the creator's own Org.
   */
  isInternalShipment: boolean;
  /** Sum of `destinationVehicleQuantities` at publish time. */
  vehicleCount: number;
  /** Number of dropoffs (`destinations.length` at publish time). */
  routeCount: number;
  /** ISO string for the start of the pickup window; aids "14:00" display. */
  pickupFromIso?: string;
}

/**
 * Bag passed into `buildCreateMultiOrderPayload`. Currently only carries
 * presentation-layer config (currency); the allocation strategy is now
 * an explicit field on the store (`allocationStrategy`) driven by the
 * `CarrierSection` on the publish page, so the previous
 * `isCarrierSideCreator` heuristic is no longer needed.
 */
export interface PublishOrderContext {
  /** Currency code applied to all items + price calc. */
  currency?: string;
}

export interface PublishOrderState {
  /**
   * Selected client, or `null` when this is an internal shipment.
   * When `isInternalShipment` is `false` the publish gate requires a
   * client to be picked — there is no "walk-in" / ad-hoc client mode
   * anymore.
   */
  customer: PublishOrderCustomerDraft | null;
  /**
   * `true` when the creator is shipping their **own goods** — no
   * external CRM client is involved. The page hides the client picker
   * in this mode and the payload omits `receiver` / `clientId`.
   * Renamed from `isWalkIn` to reflect the new semantics: every order
   * must be linked to either an explicit client OR to the creator's
   * own Org as an internal move.
   */
  isInternalShipment: boolean;
  /**
   * How this order will be sourced — picked explicitly via the
   * `CarrierSection` on the publish page. Passed through verbatim on
   * the create payload as `allocation.strategy`. When undefined the
   * backend defaults to its role-aware fallback in
   * {@link OrderTransformer.defaultStrategyForCreator}.
   */
  allocationStrategy?: OrderAllocationStrategy;
  /**
   * Phase 5 — when the user picked "Specific carrier" in the
   * `CarrierSection`, the targeted Org id is stamped here. The
   * payload builder forwards it as `allocation.targetOrgId` so
   * `OrderAllocationService.onOrderCreated` can short-circuit the
   * broadcast strategies into a single targeted Offer (skipping
   * fan-out). Cleared when the user switches strategy back to a
   * non-targeted option.
   */
  targetOrgId?: string;
  /**
   * Phase 5 — same as {@link targetOrgId} but for unsigned Clients
   * (Client docs that do NOT have a linked Org Party). The backend
   * prefers `targetOrgId` when both are set; this is reserved for
   * "send to a Client that hasn't joined Hero yet" flows.
   */
  targetClientId?: string;
  /**
   * Phase 5 — display label for the picked specific-carrier target.
   * Pure UX state for the section header chip; not sent on the wire.
   */
  targetLabel?: string;

  origin: PublishOrderLocationDraft | null;
  destinations: PublishOrderLocationDraft[];
  /** Per-destination truck count; same length as `destinations`. */
  destinationVehicleQuantities: number[];

  load: PublishOrderLoadDraft;
  /**
   * Phase 2 (relations-v2) — the subset of `load` fields that the user
   * has explicitly touched in this session. The auto-apply manager
   * (`useApplyClientOrderDefaultsManager`) uses this set to seed
   * defaults from `Org.settings` / `Client.settings` *only* into
   * fields the user hasn't decided themselves: switching the picked
   * client must not clobber an in-progress edit.
   *
   * Written by `setLoadFromUser` (every keystroke in
   * `LoadDetailsFormDrawer`); cleared by `replaceLoad` and the
   * publish-side resets so the next order can pick up fresh defaults
   * for the (possibly new) client.
   */
  loadOverrides: Partial<Record<keyof PublishOrderLoadDraft, true>>;
  /**
   * Snapshot of the org / client whose settings were last seeded into
   * `load`. The auto-apply manager skips work when neither id has
   * changed since the last seed (avoids redundant `replaceLoad`s).
   */
  loadAppliedSource?: { orgId?: string; clientId?: string };
  pickupDate: PublishOrderPickupRange | null;
  /** Optional carrier instructions added to every route. */
  instructions?: string;

  /**
   * SavedLocations created during this session (via the picker drawer's
   * "Save & use" flow). Kept here so the saved-locations selector shows
   * them immediately, before the React Query cache invalidation lands.
   */
  savedLocationsExtras: SavedLocation[];

  /**
   * Orders the carrier published during the current page visit. Used by
   * the side panel "This session" list. Cleared on `reset()` or on an
   * explicit `clearSession()` (e.g. when leaving `/dashboard/order`).
   */
  sessionOrders: PublishSessionEntry[];

  /**
   * Last successful multi-route price preview from the backend
   * resolver (`/price/calc-multi-routes`). Holds one entry per
   * destination so the per-destination price chip + side-panel
   * breakdown stay in lockstep. `null` when the lane isn't priceable
   * yet (no client / no origin / no destination / no vehicle), which
   * is a normal early-state, not an error.
   *
   * Written by `usePublishOrderPricingManager`; cleared on reset.
   */
  pricePreview: CalculatePriceMultiRoutesData | null;

  /**
   * Per-destination one-time price overrides — populated when the
   * user opens the edit-price drawer on a destination card and saves
   * a manual price. The publish payload forwards each override as
   * `routes[i].price` so the backend records `pricingStrategy:
   * 'one_time_pricing'` for that specific leg (mirrors the Order
   * Again forwarding pattern in `useOrderAgain`). Untouched
   * destinations omit the override and let the backend resolver pick
   * the price.
   *
   * Keyed by destination index (must stay in sync with
   * `destinations[]` length). Add/remove on the destinations array
   * shifts these too — see `addDestination` / `removeDestination`.
   */
  priceOverrides: Array<PublishOrderPriceOverride | undefined>;
}

/**
 * One-time price override payload — same shape the backend records
 * onto the contract leg's `Amount.itemsOriginalSubtotal` when the
 * caller forwards `routes[i].price` on `CreateMultiOrderAPI`. See
 * the `oneTimePrice` flow in `useOrderAgain` and
 * `OrderRouteService.getOrderRouteDetails`.
 */
export interface PublishOrderPriceOverride {
  /** Pre-tax load price typed by the user. */
  itemsOriginalSubtotal: number;
  /** Currency for both this override and any fees attached to it. */
  currency: Currency;
  /** When `true`, backend applies the configured tax rate on save. */
  taxable: boolean;
  /** Per-leg fees the user chose to attach (excludes auto fees like `items_insurance`). */
  fees?: Fee[];
}

export interface PublishOrderActions {
  setCustomer: (next: PublishOrderCustomerDraft | null) => void;
  setInternalShipment: (isInternal: boolean) => void;
  setAllocationStrategy: (strategy: OrderAllocationStrategy | undefined) => void;
  /**
   * Phase 5 — set the target carrier for a `private_network` /
   * `targeted` strategy. Pass `null` to clear (strategy reverts to
   * untargeted broadcast / auto cascade). Either `orgId` or
   * `clientId` must be present, not both — prefer `orgId` for
   * signed clients.
   */
  setAllocationTarget: (
    target:
      | { orgId?: string; clientId?: string; label?: string }
      | null,
  ) => void;

  setOrigin: (next: PublishOrderLocationDraft | null) => void;
  setDestination: (index: number, next: PublishOrderLocationDraft) => void;
  addDestination: () => void;
  removeDestination: (index: number) => void;
  setDestinationVehicleQuantity: (index: number, quantity: number) => void;

  setLoad: (patch: Partial<PublishOrderLoadDraft>) => void;
  /**
   * Phase 2 (relations-v2) — same as {@link setLoad} but also marks
   * each touched key in {@link loadOverrides} so the auto-apply manager
   * leaves it alone on subsequent client switches. Use this from any
   * UI control that represents an explicit user decision (the
   * `LoadDetailsFormDrawer` selectors / inputs).
   */
  setLoadFromUser: (patch: Partial<PublishOrderLoadDraft>) => void;
  /**
   * Phase 2 (relations-v2) — apply derived defaults from
   * `{ ...orgSettings, ...clientSettings }` (business creators) or
   * `Org.settings.vehicle_preferred_types` (carrier creators). Only
   * writes to fields the user hasn't already touched
   * (`loadOverrides[key]` falsy).
   *
   * `source` lets the manager skip redundant calls when neither org
   * nor client has changed since the last seed.
   */
  applyLoadDefaults: (input: {
    defaults: Partial<PublishOrderLoadDraft>;
    /** When omitted, defaults are merged without updating {@link loadAppliedSource}. */
    source?: { orgId?: string; clientId?: string };
  }) => void;
  replaceLoad: (next: PublishOrderLoadDraft) => void;

  setPickupDate: (next: PublishOrderPickupRange | null) => void;
  setInstructions: (next: string | undefined) => void;

  /** Track a freshly created SavedLocation in-session. */
  addSavedLocationExtra: (loc: SavedLocation) => void;

  /** Append a published-order snapshot to the side panel session list. */
  pushSessionEntry: (entry: PublishSessionEntry) => void;
  /** Drop every session entry (e.g. "Clear" affordance, page unmount). */
  clearSession: () => void;

  /** Replace the latest backend price preview (called by the pricing manager). */
  setPricePreview: (preview: CalculatePriceMultiRoutesData | null) => void;
  /**
   * Set / clear a one-time price override for a single destination.
   * Pass `undefined` (or omit `override`) to clear an existing one
   * and revert to the resolver-provided price for that leg.
   */
  setPriceOverride: (
    index: number,
    override: PublishOrderPriceOverride | undefined,
  ) => void;

  /**
   * Soft reset for batch entry: clears the per-order fields (customer,
   * destinations, item-level load, instructions) but keeps "lane setup"
   * sticky — origin, pickupDate, vehicleType, temperature, packing — so
   * the carrier can fly through 5+ orders for the same lane.
   *
   * Pass `keepSticky: false` to behave like the full `reset` but without
   * touching `sessionOrders` / `savedLocationsExtras`.
   */
  resetForNext: (opts?: { keepSticky?: boolean }) => void;

  reset: () => void;
}

export type PublishOrderStore = PublishOrderState & PublishOrderActions;

/**
 * Empty location placeholder used to seed the destinations array so the
 * form always renders at least one destination slot (per UX rule: a route
 * is always 1 origin + 1 destination by default). `lat === 0` is the
 * sentinel "not picked yet" that the publish-readiness selector checks.
 *
 * Exported so the order-draft factory (`createOrderDraftStore`) can
 * compose the same defaults without forking the shape.
 */
export const emptyLocation = (): PublishOrderLocationDraft => ({ name: '', lat: 0, lng: 0 });

/**
 * Defensive deep-clone for store-internal use. We use `structuredClone`
 * when available, and fall back to a JSON round-trip if the source
 * graph carries a non-cloneable value (the most common offender is a
 * `bson.ObjectId` instance that some lib versions decorate with
 * symbol-keyed metadata). The fallback drops methods / prototypes,
 * which is FINE for the in-store data slices because they are
 * presentation-layer drafts only — any field we read by hex string
 * (e.g. `customerId`, `_id` of a saved location) survives the
 * round-trip as a string.
 */
const safeClone = <T>(value: T): T => {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value)) as T;
  }
};

/**
 * Keep `vehicleType` (singular) and `vehicleTypes` (plural) aligned
 * inside a single load patch. The publish flow has both fields for
 * back-compat — readers like pricing / save-as-default look at the
 * singular form, while the new multi-select writes the plural one.
 * Whichever side a caller writes, this helper mirrors the change
 * onto the other so neither view of the load goes stale.
 */
function syncVehicleTypeFields(
  patch: Partial<PublishOrderLoadDraft>,
): Partial<PublishOrderLoadDraft> {
  if ('vehicleTypes' in patch) {
    const types = patch.vehicleTypes ?? [];
    return { ...patch, vehicleType: types[0] };
  }
  if ('vehicleType' in patch) {
    return {
      ...patch,
      vehicleTypes: patch.vehicleType ? [patch.vehicleType] : [],
    };
  }
  return patch;
}

/**
 * Initial `PublishOrderState` used both by the legacy singleton
 * (`usePublishOrderStore`) and by the order-draft factory
 * (`createOrderDraftStore`). Cloned per call to avoid sharing
 * mutable array references between instances.
 */
export const createInitialPublishOrderState = (): PublishOrderState => ({
  customer: null,
  isInternalShipment: false,
  allocationStrategy: 'auto',
  origin: null,
  destinations: [emptyLocation()],
  destinationVehicleQuantities: [1],
  load: {},
  loadOverrides: {},
  loadAppliedSource: undefined,
  pickupDate: null,
  instructions: undefined,
  savedLocationsExtras: [],
  sessionOrders: [],
  pricePreview: null,
  priceOverrides: [undefined],
});

const initialState: PublishOrderState = createInitialPublishOrderState();

type PublishStoreSet = Parameters<
  Parameters<typeof immer<PublishOrderStore>>[0]
>[0];

/**
 * Build the action map for a `PublishOrderStore`. Exported so the
 * order-draft factory (`createOrderDraftStore`) can compose the
 * same behavior without duplicating logic — the legacy singleton
 * below and every scoped instance share one implementation.
 */
export const createPublishOrderActions = (
  set: PublishStoreSet,
): PublishOrderActions => ({
    setCustomer: next =>
      set(state => {
        state.customer = next;
        if (next) state.isInternalShipment = false;
      }),
    setInternalShipment: isInternal =>
      set(state => {
        state.isInternalShipment = isInternal;
        if (isInternal) state.customer = null;
      }),
    setAllocationStrategy: strategy =>
      set(state => {
        state.allocationStrategy = strategy;
        // Switching off a targeted strategy clears the recipient
        // ids. The picker UI re-prompts when re-targeting.
        if (strategy !== 'private_network') {
          state.targetOrgId = undefined;
          state.targetClientId = undefined;
          state.targetLabel = undefined;
        }
      }),
    setAllocationTarget: target =>
      set(state => {
        state.targetOrgId = target?.orgId;
        state.targetClientId = target?.orgId ? undefined : target?.clientId;
        state.targetLabel = target?.label;
      }),

    setOrigin: next =>
      set(state => {
        state.origin = next;
      }),
    setDestination: (index, next) =>
      set(state => {
        // Allow setDestination(0, ...) to grow the array if needed.
        while (state.destinations.length <= index) {
          state.destinations.push({ name: '', lat: 0, lng: 0 });
          state.destinationVehicleQuantities.push(1);
          state.priceOverrides.push(undefined);
        }
        state.destinations[index] = next;
      }),
    addDestination: () =>
      set(state => {
        state.destinations.push({ name: '', lat: 0, lng: 0 });
        state.destinationVehicleQuantities.push(1);
        state.priceOverrides.push(undefined);
      }),
    removeDestination: index =>
      set(state => {
        // Keep the invariant: at least one destination slot is always
        // present. Removing the last row clears it back to the empty
        // placeholder so the UI still shows "Pick destination 1".
        if (state.destinations.length <= 1) {
          state.destinations[0] = emptyLocation();
          state.destinationVehicleQuantities[0] = 1;
          state.priceOverrides[0] = undefined;
          return;
        }
        state.destinations.splice(index, 1);
        state.destinationVehicleQuantities.splice(index, 1);
        state.priceOverrides.splice(index, 1);
      }),
    setDestinationVehicleQuantity: (index, quantity) =>
      set(state => {
        const clamped = Math.max(1, Math.floor(quantity));
        if (state.destinationVehicleQuantities[index] !== undefined) {
          state.destinationVehicleQuantities[index] = clamped;
        }
      }),

    setLoad: patch =>
      set(state => {
        state.load = { ...state.load, ...syncVehicleTypeFields(patch) };
      }),
    setLoadFromUser: patch =>
      set(state => {
        const synced = syncVehicleTypeFields(patch);
        state.load = { ...state.load, ...synced };
        // Mark every key the caller mentioned (regardless of value) so
        // a later auto-apply leaves it alone — including a deliberate
        // "clear" (e.g. `setLoadFromUser({ itemCost: undefined })`).
        for (const key of Object.keys(synced) as Array<keyof PublishOrderLoadDraft>) {
          state.loadOverrides[key] = true;
        }
      }),
    applyLoadDefaults: ({ defaults, source }) =>
      set(state => {
        const next: PublishOrderLoadDraft = { ...state.load };
        for (const key of Object.keys(defaults) as Array<keyof PublishOrderLoadDraft>) {
          if (state.loadOverrides[key]) continue;
          (next as Record<string, unknown>)[key] = defaults[key] as never;
        }
        state.load = next;
        if (source) {
          state.loadAppliedSource = source;
        }
      }),
    replaceLoad: next =>
      set(state => {
        state.load = next;
        // A wholesale replace counts as the user committing to every
        // visible field, so wipe overrides + source. The next
        // auto-apply call will re-seed against the new baseline.
        state.loadOverrides = {};
        state.loadAppliedSource = undefined;
      }),

    setPickupDate: next =>
      set(state => {
        state.pickupDate = next;
      }),
    setInstructions: next =>
      set(state => {
        state.instructions = next;
      }),

    addSavedLocationExtra: loc =>
      set(state => {
        // Replace-by-id semantics: an update flow re-emits the same
        // `_id` and we want the drawer's saved-locations list to
        // reflect the latest record, not stack a duplicate next to
        // the stale one.
        const id = String(loc._id);
        state.savedLocationsExtras = state.savedLocationsExtras.filter(
          existing => String(existing._id) !== id,
        );
        state.savedLocationsExtras.push(loc);
      }),

    pushSessionEntry: entry =>
      set(state => {
        state.sessionOrders.unshift(entry);
      }),
    clearSession: () =>
      set(state => {
        state.sessionOrders = [];
      }),

    setPricePreview: preview =>
      set(state => {
        state.pricePreview = preview;
      }),
    setPriceOverride: (index, override) =>
      set(state => {
        // Grow the overrides array to match if a caller writes to a
        // destination that's about to be added. Keeps the array
        // length aligned with `destinations[]` so the publish payload
        // builder can index by destination.
        while (state.priceOverrides.length <= index) {
          state.priceOverrides.push(undefined);
        }
        state.priceOverrides[index] = override;
      }),

    resetForNext: (opts = {}) =>
      set(state => {
        const keepSticky = opts.keepSticky !== false;
        // Snapshot the parts we want to preserve as plain data BEFORE we
        // overwrite state — immer drafts can't be safely re-attached after
        // an Object.assign on the root, so we deep-clone first. `safeClone`
        // falls back to a JSON round-trip if `structuredClone` chokes on a
        // non-cloneable value (e.g. an `ObjectId` instance carrying
        // symbol-keyed metadata) so the publish reset never crashes.
        const stickyOrigin =
          keepSticky && state.origin ? safeClone(state.origin) : null;
        const stickyPickupDate =
          keepSticky && state.pickupDate
            ? { from: state.pickupDate.from, to: state.pickupDate.to }
            : null;
        const stickyLoad: PublishOrderLoadDraft = keepSticky
          ? {
              vehicleType: state.load.vehicleType,
              vehicleTypes: state.load.vehicleTypes
                ? [...state.load.vehicleTypes]
                : state.load.vehicleType
                ? [state.load.vehicleType]
                : undefined,
              temperatureType: state.load.temperatureType,
              temperatureDegree: state.load.temperatureDegree
                ? { ...state.load.temperatureDegree }
                : undefined,
              packing: state.load.packing,
            }
          : {};
        const stickyStrategy = keepSticky ? state.allocationStrategy : undefined;
        const stickyTargetOrgId = keepSticky ? state.targetOrgId : undefined;
        const stickyTargetClientId = keepSticky ? state.targetClientId : undefined;
        const stickyTargetLabel = keepSticky ? state.targetLabel : undefined;
        const stickyInternal = keepSticky ? state.isInternalShipment : false;
        const preservedSession = safeClone(state.sessionOrders);
        const preservedExtras = safeClone(state.savedLocationsExtras);

        Object.assign(state, safeClone(initialState));
        state.sessionOrders = preservedSession;
        state.savedLocationsExtras = preservedExtras;
        // Pricing preview + per-destination overrides always reset
        // alongside the destinations themselves — the next order's
        // pricing manager will refire against the new lane.
        state.pricePreview = null;
        state.priceOverrides = [undefined];
        if (keepSticky) {
          state.origin = stickyOrigin;
          state.pickupDate = stickyPickupDate;
          state.load = stickyLoad;
          // Allocation + internal-shipment are session-level intent: a
          // carrier publishing five "own fleet" loads in a row shouldn't
          // have to repick `own_fleet` every time.
          state.allocationStrategy = stickyStrategy ?? 'auto';
          state.targetOrgId = stickyTargetOrgId;
          state.targetClientId = stickyTargetClientId;
          state.targetLabel = stickyTargetLabel;
          state.isInternalShipment = stickyInternal;
        }
        // Phase 2 — `loadOverrides` / `loadAppliedSource` always reset:
        // the next order's auto-apply manager will seed defaults again
        // against the (possibly new) client. Even when sticky lane
        // fields survive, they survive AS the user's decision — but
        // we re-derive everything else against the new baseline.
        state.loadOverrides = {};
        state.loadAppliedSource = undefined;
      }),

    reset: () =>
      set(state => {
        Object.assign(state, safeClone(initialState));
      }),
});

/**
 * The single shared store for the publish-order flow. Pull what you need
 * via a selector to keep re-renders tight:
 *
 *   const customer = usePublishOrderStore(s => s.customer);
 *   const setOrigin = usePublishOrderStore(s => s.setOrigin);
 *
 * Scoped instances for agent / reorder / edit flows use
 * `createOrderDraftStore` (same factory, isolated state).
 */
export const usePublishOrderStore = create<PublishOrderStore>()(
  immer(set => ({
    ...initialState,
    ...createPublishOrderActions(set),
  })),
);

//
// ─── Selectors ────────────────────────────────────────────────────────
//

/** Sum of truck counts across all destinations (min 0 when no destinations). */
export const selectTotalVehicles = (state: PublishOrderStore): number =>
  state.destinationVehicleQuantities.reduce((sum, n) => sum + (n || 0), 0);

/** Lightweight readiness gate used by the publish button + side panel CTA. */
export const selectIsReadyToPublish = (state: PublishOrderStore): boolean => {
  // Every order must be linked to either a CRM client OR be explicitly
  // marked as an internal shipment (creator ships their own goods). No
  // ad-hoc / walk-in path anymore — that was the legacy escape hatch.
  if (!state.isInternalShipment && !state.customer?.customerId) return false;
  if (!state.origin || state.origin.lat === 0) return false;
  if (state.destinations.length === 0) return false;
  if (state.destinations.some(d => !d.name || d.lat === 0)) return false;
  if (!state.pickupDate) return false;
  // Phase 2 (relations-v2): vehicle + load detail are no longer required
  // at publish time. The backend resolves a vehicle from the creator
  // Org's `settings.vehicle_preferred_types` (or the picked Client's,
  // for business creators); items default to `[]` and totals stay at
  // zero. Users who want to publish a fully-detailed order still can —
  // they just no longer have to. See `OrderFetcher.resolveVehiclesForCreate`.
  return true;
};

/**
 * Aggregate stats for the side-panel "This session" header (count of
 * published submissions + cumulative truck and order counts).
 */
export const selectSessionStats = (state: PublishOrderStore) => {
  const submissions = state.sessionOrders.length;
  let orders = 0;
  let trucks = 0;
  for (const entry of state.sessionOrders) {
    orders += entry.orderIds.length;
    trucks += entry.vehicleCount;
  }
  return { submissions, orders, trucks };
};

/** Returns the destinations whose pin the user flagged as approximate. */
export const selectApproximateStops = (
  state: PublishOrderStore,
): PublishOrderLocationDraft[] => {
  const stops: PublishOrderLocationDraft[] = [];
  if (state.origin && state.origin.isAccurate === false) stops.push(state.origin);
  state.destinations.forEach(d => {
    if (d.lat !== 0 && d.isAccurate === false) stops.push(d);
  });
  return stops;
};

//
// ─── Payload builder ──────────────────────────────────────────────────
//

/**
 * Convert the current draft into the canonical `CreateMultiOrderAPI` body.
 * Each destination becomes its own route (pairing the shared origin →
 * that destination), matching the customer-dashboard payload shape.
 *
 * Phase 1 (Relations-v2): the payload stamps `clientId` on every route
 * (from `state.customer.customerId` when the carrier picked a CRM
 * client) and an explicit `allocation.strategy` derived from `context`
 * (carrier-side creators publish `own_fleet`; everything else publishes
 * `auto`). Both fields drive backend behaviour in
 * `OrderTransformer.generateOrder` — see
 * `docs/relations-v2/06-scenarios.md` (sub-sections A2 / C-contracts /
 * C3-contracts / E2).
 *
 * Phase 2 (Relations-v2): `vehicles` and `items` are now optional on
 * the wire. Empty `vehicles` lets the backend resolve from the creator
 * Org's `settings.vehicle_preferred_types[0]` (or the picked Client's
 * settings, for business creators); empty `items` produces an Order
 * with no item detail and zero totals. The publish gate
 * (`selectIsReadyToPublish`) only requires origin + destinations +
 * pickup + (client OR internal). See `OrderFetcher.resolveVehiclesForCreate`.
 *
 * Throws if non-load required fields are missing — callers should
 * guard on `selectIsReadyToPublish` before invoking.
 */
export function buildCreateMultiOrderPayload(
  // Data-only slice: the builder never calls actions, and non-store
  // callers (e.g. reorder's hydrated snapshot) pass plain state.
  state: PublishOrderState,
  // `_context` is kept on the signature for forward-compatibility
  // (currency, future locale overrides). The allocation strategy used
  // to live here but is now an explicit field on the store
  // (`state.allocationStrategy`) driven by the `CarrierSection` UI.
  _context: PublishOrderContext = {},
): CreateMultiOrderAPI['req'] {
  if (!state.origin) throw new Error('publish-order: origin is required');
  if (!state.pickupDate) throw new Error('publish-order: pickupDate is required');
  if (state.destinations.length === 0) {
    throw new Error('publish-order: at least one destination is required');
  }

  const pickupDate = {
    from: new Date(state.pickupDate.from),
    to: new Date(state.pickupDate.to),
  };

  // Phase 2 — items become optional. We only emit an item entry when
  // the user filled in *something* (item type or description). An empty
  // load is fine; backend tolerates `items: []` (zero totals, no
  // insurance) and the publish gate has dropped its `itemType` check.
  const hasAnyItemDetail = Boolean(
    state.load.itemType ||
      state.load.itemDescription?.trim() ||
      state.load.weightTon ||
      state.load.itemCost ||
      state.load.quantity,
  );
  const items: CreateMultiOrderAPI['req']['routes'][number]['items'] = hasAnyItemDetail
    ? [
        {
          name:
            state.load.itemDescription?.trim() ||
            state.load.itemType ||
            'other',
          weights: state.load.weightTon
            ? { value: state.load.weightTon, type: 'ton' as const }
            : undefined,
          quantity: state.load.quantity || 1,
          packing: state.load.packing ?? 'pallet',
          type: state.load.itemType ?? 'other',
          cost: state.load.itemCost || 0,
        },
      ]
    : [];

  const temperature: CreateMultiOrderAPI['req']['routes'][number]['temperature'] = {
    types: [
      (state.load.temperatureType ?? 'dry') as
        | 'chilled'
        | 'frozen'
        | 'hot'
        | 'dry',
    ],
    degree: state.load.temperatureDegree,
  };

  // `receiver` is not sent from the publish UI. The backend resolves
  // `Order.sender` and `Order.receiver` from the picked Client's buyer
  // Party (`OrderSelector.buyerBeneficiary`). Internal shipments leave
  // both unset.
  const origin = state.origin;
  const instructions = state.instructions?.trim() || undefined;

  // CRM client id — when present, threaded through pricing
  // (`OrderRouteService.buyerId`), into tenants, and (gated on
  // strategy below) into the seeded contract. Internal shipments
  // leave this undefined so the server treats the order as an
  // own-org move (no external buyer party).
  const clientId =
    !state.isInternalShipment && state.customer?.customerId
      ? getObjectId(state.customer.customerId)
      : undefined;

  // Allocation strategy picked explicitly on the `CarrierSection`.
  // The store seeds this with `'auto'` so the publish gate always has
  // a value; the user can change it before publishing. When the gate
  // in `OrderTransformer.generateOrder` evaluates the payload, only
  // `own_fleet` + known buyer Party seeds `contracts[0]`; everywhere
  // else the Offer cascade owns leg creation.
  const allocationStrategy: OrderAllocationStrategy = state.allocationStrategy ?? 'auto';
  // Phase 5 — targeted carrier ids forwarded only when the user
  // picked a `private_network` strategy WITH a specific recipient.
  // The backend's `OrderAllocationService.onOrderCreated` reads
  // these and creates a single targeted Offer instead of broadcasting.
  const targetOrgId =
    allocationStrategy === 'private_network' && state.targetOrgId
      ? getObjectId(state.targetOrgId)
      : undefined;
  const targetClientId =
    allocationStrategy === 'private_network' && !state.targetOrgId && state.targetClientId
      ? getObjectId(state.targetClientId)
      : undefined;

  // Phase 2 — vehicles is optional on the wire. Prefer the plural
  // `vehicleTypes` array (set by the multi-select in the load
  // drawer) when populated, so a user that picked "flatbed OR
  // curtain side" actually sends both. Fall back to the singular
  // `vehicleType` for back-compat with code paths that still write
  // the legacy field. Empty array signals the backend to resolve
  // vehicles from org/client settings; the publish gate no longer
  // rejects orders with no vehicle picked.
  const vehicles: CreateMultiOrderAPI['req']['routes'][number]['vehicles'] =
    state.load.vehicleTypes && state.load.vehicleTypes.length > 0
      ? (state.load.vehicleTypes as VehicleTypes[])
      : state.load.vehicleType
      ? [state.load.vehicleType as VehicleTypes]
      : [];

  const routes: CreateMultiOrderAPI['req']['routes'] = state.destinations.map((dest, idx) => {
    // Per-destination one-time price override — set when the user
    // edited the price chip on this destination card. Forwarded as
    // `routes[i].price` so the backend records `one_time_pricing`
    // for that leg specifically. Untouched destinations omit the
    // override and let the resolver pick the price.
    const override = state.priceOverrides[idx];
    return {
      pickupDate,
      vehicleQuantity: state.destinationVehicleQuantities[idx] || 1,
      vehicles,
      temperature,
      items,
      instructions,
      destinations: [
        {
          type: 'pickup' as const,
          location: { lat: origin.lat, lng: origin.lng },
          savedLocationId: origin.savedLocationId
            ? getObjectId(origin.savedLocationId)
            : undefined,
          isAccurateLocation: origin.savedLocationId ? true : origin.isAccurate ?? false,
          locationUrl: origin.locationUrl,
        },
        {
          type: 'dropoff' as const,
          location: { lat: dest.lat, lng: dest.lng },
          savedLocationId: dest.savedLocationId ? getObjectId(dest.savedLocationId) : undefined,
          isAccurateLocation: dest.savedLocationId ? true : dest.isAccurate ?? false,
          locationUrl: dest.locationUrl,
        },
      ],
      clientId,
      allocation: {
        strategy: allocationStrategy,
        ...(targetOrgId ? { targetOrgId } : {}),
        ...(targetClientId ? { targetClientId } : {}),
      },
      // Only the subtotal / currency / taxable triple is accepted on
      // the create payload (`createOrderAPISchema.price`). Fees and
      // payment method tracked locally on the override are presented
      // in the side panel breakdown but persisted only after the
      // order is created (via the order drawer's edit-price drawer).
      ...(override
        ? {
            price: {
              itemsOriginalSubtotal: override.itemsOriginalSubtotal,
              currency: override.currency,
              taxable: override.taxable,
            },
          }
        : {}),
      ...(state.load.podSettings ? { podSettings: state.load.podSettings } : {}),
    };
  });

  return { routes };
}
